import React, { useContext, useEffect, useState } from "react";
import { Switch } from "@mui/material";
import styles from "./styles/PartnerSharing.module.css";
import { switchSx } from "./PartnerSharing";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import {
  PERSON_SHARING_FLAGS,
  personSharingFlags,
} from "../../../Utils/partnerPropose";

/*
  Partner Sharing for a person — the closing section of the Add / Edit
  Beneficiary and Add / Edit Trusted Advisor modals.

  Two switches, not the asset section's master-plus-four. Measured off
  benef.svg: a divider, then two rows of the same 13px label and the same
  No | switch | Yes group as the asset section, which is why this shares that
  component's stylesheet and switch styling rather than restating either.

  The first switch decides whether the person may be named to the partner at
  all; the second adds their email addresses and phone number, and means
  nothing on its own. Turning the first off turns the second off with it —
  leaving a stale true behind would be a promise the label does not make, even
  though the server ignores it (see the whitelist in Routes/PartnerUser.js).

  What is NOT here is the point of it. A beneficiary's date of birth, personal
  message, identity documents and photo have no switch, and an advisor's
  additional information has none either. There being no flag is exactly why
  the server never sends them.
*/

/*
  Each row's View flag and the Edit flag that answers to it, per subject.

  A beneficiary has five rows and an advisor two, so they no longer share a
  shape. Two of the beneficiary's columns are fixed rather than switched, and
  both are rules rather than defaults:

  - The master row's View is always Yes. Sharing somebody at all is what
    makes their name and relation readable; a switch offering to withhold the
    name of a person you have just agreed to share would answer a question
    nobody asked.
  - The contact row's Edit is always No. An address or a phone number is how
    this person will actually be reached, and getting one wrong is not a
    thing to delegate. There is no `edit_contact_with_partner` on a
    beneficiary at all -- not on the schema, not in the whitelist -- so the
    column prints the word instead of a control.
*/
export const BENEFICIARY_EDIT_FLAG_OF = {
  share_with_partner: "edit_with_partner",
  // share_contact_with_partner: view only, deliberately absent.
  share_message_with_partner: "edit_message_with_partner",
  share_picture_with_partner: "edit_picture_with_partner",
  share_trustees_with_partner: "edit_trustees_with_partner",
  share_assets_with_partner: "edit_assets_with_partner",
};

/*
  An advisor's four, the same shape as a beneficiary's. Their contact
  details are view-only too, so there is no pair for that row.
*/
export const PERSON_EDIT_FLAG_OF = {
  share_with_partner: "edit_with_partner",
  share_message_with_partner: "edit_message_with_partner",
  share_assets_with_partner: "edit_assets_with_partner",
  share_beneficiaries_with_partner: "edit_beneficiaries_with_partner",
};

/*
  Every flag off, which is where any person starts: sharing who a
  beneficiary is has never implied letting somebody rename them.

  Built from the canonical list in Utils/partnerPropose rather than typed
  out, so a row added there is carried here, in both modals' state, in
  their saves, and through decryptBeneficiary/decryptTrustee without any of
  them being edited.
*/
export const EMPTY_PERSON_SHARING = personSharingFlags({});

/*
  The sharing flags off a record, and nothing else.

  Every caller passed a hand-written pair of share flags and left the edit
  flags out, so the Edit column could never show what was stored -- it drew
  off on a permission the customer had granted. Taken from the starting
  shape instead, so the next flag added is carried by all four modals
  without any of them being touched.

  `=== true` throughout: a record saved before a flag existed has granted
  nothing, and `undefined` is not a permission.
*/
export const personSharingValue = (record) => personSharingFlags(record);

const FALLBACK = {
  partnerSharingText8: "No",
  partnerSharingText9: "Yes",
  partnerSharingText11: "{ partner name }",
  partnerSharingText19: "Allow {name} to:",
  partnerSharingText20: "View",
  partnerSharingText21: "Edit",
  partnerSharingText14: "Share the Trusted Advisor name  with {name}",
  partnerSharingText15: "Share the Trusted Advisor contact details  with {name}",
  partnerSharingText16:
    "{name} can\u2019t edit the contact details, and can\u2019t see the beneficiary\u2019s birthday. You can modify this at anytime.",
  partnerSharingText17: "Their additional information is never shared.",
  partnerSharingText23: "Share the beneficiary with {name}",
  partnerSharingText24: "Beneficiary\u2019s name and relation",
  partnerSharingText25: "Beneficiary\u2019s contact details",
  partnerSharingText26: "Beneficiary\u2019s private message and files",
  partnerSharingText27: "Beneficiary\u2019s picture",
  partnerSharingText28: "Beneficiary\u2019s trusted advisors",
  partnerSharingText35: "Beneficiary\u2019s assets",
  partnerSharingText36: "Shared with you",
  partnerSharingText37: "You may:",
};

/*
  The two subjects, and what now differs between them: how many rows there
  are and whether a master switch sits above them.

  Both read like an asset now: one switch that shares the person at all, and
  beneath it a line per thing the partner may be allowed to see or change.
  Five rows each, differing only in what those rows cover -- a beneficiary's
  photo and advisors, an advisor's message and the records they are attached
  to.

  `view: null` on a row means the column states Yes rather than offering a
  switch, and `edit: null` means it states No. Both are rules, not defaults:
  see BENEFICIARY_EDIT_FLAG_OF above.
*/
const SUBJECTS = {
  beneficiary: {
    flagOf: BENEFICIARY_EDIT_FLAG_OF,
    masterKey: "partnerSharingText23",
    noteKey: "partnerSharingText16",
    rows: [
      // Shared at all means readable: the master IS this row's View.
      { labelKey: "partnerSharingText24", view: null, edit: "edit_with_partner" },
      { labelKey: "partnerSharingText25", view: "share_contact_with_partner", edit: null },
      {
        labelKey: "partnerSharingText26",
        view: "share_message_with_partner",
        edit: "edit_message_with_partner",
      },
      {
        labelKey: "partnerSharingText27",
        view: "share_picture_with_partner",
        edit: "edit_picture_with_partner",
      },
      {
        labelKey: "partnerSharingText28",
        view: "share_trustees_with_partner",
        edit: "edit_trustees_with_partner",
      },
      /*
        Which assets this beneficiary inherits.

        View shows the partner the ones already shared with them as records
        and no others, so the row discloses the link rather than the asset:
        an asset they were not given stays unmentioned, and the count above
        the list never includes it. Edit lets them propose a change to that
        subset, which the customer's modal merges rather than applies --
        without that merge the withheld ones would be dropped on accept.
      */
      {
        labelKey: "partnerSharingText35",
        view: "share_assets_with_partner",
        edit: "edit_assets_with_partner",
      },
    ],
  },
  trustee: {
    flagOf: PERSON_EDIT_FLAG_OF,
    masterKey: "partnerSharingText29",
    noteKey: "partnerSharingText17",
    rows: [
      // Shared at all means readable: the master IS this row's View.
      { labelKey: "partnerSharingText30", view: null, edit: "edit_with_partner" },
      { labelKey: "partnerSharingText31", view: "share_contact_with_partner", edit: null },
      {
        labelKey: "partnerSharingText32",
        view: "share_message_with_partner",
        edit: "edit_message_with_partner",
      },
      {
        labelKey: "partnerSharingText33",
        view: "share_assets_with_partner",
        edit: "edit_assets_with_partner",
      },
      {
        labelKey: "partnerSharingText34",
        view: "share_beneficiaries_with_partner",
        edit: "edit_beneficiaries_with_partner",
      },
    ],
  },
};

/*
  `viewer` is who is reading the panel, and it changes the words rather than
  the rows.

  For the CUSTOMER it is a control: "Share the beneficiary with {partner}",
  "Allow {partner} to:", and a footnote ending "You can modify this at
  anytime." For the PARTNER it is a statement of what they were given, and
  every one of those sentences is wrong for them -- they cannot modify it,
  and their own name read back at them ("Autoriser vous a :") is not how
  either language says it. So that side gets its own three strings and drops
  the footnote, which only addresses the person who can change things.

  The rows, the View/Edit headings and the Yes/No words are the same for
  both: they name what the permission IS, not who is granting it.
*/
function PartnerSharingPerson({
  subject = "beneficiary",
  value,
  onChange,
  disabled = false,
  partnerName,
  viewer = "customer",
}) {
  const asPartner = viewer === "partner";
  const websiteCurrentLang = useContext(LanguageContext).language;
  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || FALLBACK
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.EditAsset?.PartnerSharing || FALLBACK
    );
  }, [websiteCurrentLang]);
  const t = (key) => (content && content[key]) || FALLBACK[key];

  const [internal, setInternal] = useState(value || EMPTY_PERSON_SHARING);
  // Controlled when the parent passes `value`, uncontrolled otherwise.
  const sharing = value || internal;

  const keys = SUBJECTS[subject] || SUBJECTS.beneficiary;

  const update = (patch) => {
    let next = { ...sharing, ...patch };
    /*
      Edit implies View, both ways: granting an edit grants the read it
      needs, and withdrawing the read withdraws the edit rather than leaving
      a permission parked for the next time this is shared. The server checks
      the same pair on every proposal.
    */
    for (const [view, edit] of Object.entries(keys.flagOf)) {
      if (patch[edit] === true) next[view] = true;
      if (next[view] !== true) next[edit] = false;
    }
    /*
      Nothing outlives the master. Every other flag goes with it rather than
      sitting true under a closed one, which the server would ignore but
      which would come back the next time this person was shared.
    */
    if (next.share_with_partner !== true) {
      next = { ...next, ...EMPTY_PERSON_SHARING };
    }
    if (!value) setInternal(next);
    if (onChange) onChange(next);
  };

  const name = String(partnerName || "").trim() || t("partnerSharingText11");
  const withName = (key) => t(key).replace("{name}", name);

  const isOn = Boolean(sharing.share_with_partner);

  const renderToggle = (field, label, rowDisabled) => (
    <div className={styles.toggle}>
      <span className={styles.toggle_side}>{t("partnerSharingText8")}</span>
      <Switch
        sx={switchSx}
        /*
          Drawn off while the row is inert, whatever the record holds: with
          the master off nothing is shared, and a switch sitting on would be
          claiming something that is not true. Display only -- the stored
          value is left alone, because `update` clears it anyway.
        */
        checked={rowDisabled ? false : Boolean(sharing[field])}
        disabled={disabled || rowDisabled}
        onChange={(e) => update({ [field]: e.target.checked })}
        inputProps={{ "aria-label": label }}
      />
      <span className={styles.toggle_side}>{t("partnerSharingText9")}</span>
    </div>
  );

  // A column that states its answer instead of offering a switch.
  const renderFixed = (key) => (
    <div className={styles.fixed_value}>{t(key)}</div>
  );

  return (
    <div className={styles.section}>
      <div className={styles.rows}>
        {keys.masterKey && (
          <div className={styles.row}>
            <div className={styles.title}>
              {asPartner
                ? t("partnerSharingText36")
                : withName(keys.masterKey)}
            </div>
            {renderToggle(
              "share_with_partner",
              withName(keys.masterKey),
              false
            )}
          </div>
        )}

        {/*
          Two columns: what the partner may read, and separately what they
          may change. A row can be neither.
        */}
        <div
          className={`${styles.row} ${styles.detail_row} ${
            styles.column_head
          } ${keys.masterKey && !isOn ? styles.row_disabled : ""}`}
        >
          <div className={keys.masterKey ? styles.detail_label : styles.title}>
            {keys.masterKey
              ? asPartner
                ? t("partnerSharingText37")
                : withName("partnerSharingText19")
              : ""}
          </div>
          <div className={styles.column_label}>{t("partnerSharingText20")}</div>
          <div className={styles.column_label}>{t("partnerSharingText21")}</div>
        </div>

        {/*
          Every row is always rendered, greyed and inert until the person is
          shared. Hidden, they made one switch look like the whole of the
          choice: nothing on screen said that what a partner may read is
          itself a set of decisions until you had already agreed to share.
        */}
        {keys.rows.map((row) => {
          /*
            A row whose own View flag IS the master is never inert -- it is
            the switch that turns the rest on. On the advisor's panel that is
            the first row; on a beneficiary's the master sits above the list
            and every row below waits for it.
          */
          const inert =
            row.view === "share_with_partner" ? false : !isOn;
          const label = withName(row.labelKey);
          return (
            <div
              className={`${styles.row} ${styles.detail_row} ${
                inert ? styles.row_disabled : ""
              }`}
              key={row.labelKey}
            >
              <div
                className={keys.masterKey ? styles.detail_label : styles.title}
              >
                {label}
              </div>
              {row.view
                ? renderToggle(row.view, label, inert)
                : renderFixed("partnerSharingText9")}
              {row.edit
                ? renderToggle(
                    row.edit,
                    `${label} \u2014 ${t("partnerSharingText21")}`,
                    inert
                  )
                : renderFixed("partnerSharingText8")}
            </div>
          );
        })}
      </div>

      {/*
        Always shown, on or off.

        It states the two things this section cannot be made to do -- the
        contact details are never editable and the date of birth is never
        shared -- so it is most useful to somebody deciding whether to turn
        sharing on, which is precisely when it used to be hidden. Every row
        above it is always present too (disabled rather than removed), so
        nothing about the section's height moves when a switch is flipped.
      */}
      {/*
        Dropped for the partner: it closes with "You can modify this at
        anytime", which is an offer to the customer and untrue of them.
      */}
      {!asPartner && (
        <div className={styles.person_note}>{withName(keys.noteKey)}</div>
      )}
    </div>
  );
}

export default PartnerSharingPerson;
